import React, { useState } from 'react';
import { Controller, useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import {
  IconButton,
  InputAdornment,
  Typography,
  styled,
  Box,
} from '@material-ui/core';
import { usePostHog } from 'posthog-js/react';

import { Visibility, VisibilityOff } from '@material-ui/icons';

import { useMutation } from '@apollo/client';

import { useParams, useLocation } from 'react-router-dom';

import { SleepioWellnessOnboardingParams } from '../../SleepioWellnessOnboardingPage';

import { getPasswordValidationState } from './helper/getPasswordValidationState';

import sparkTheme from '@/themes/sparkTheme';

import Button from '@/components/Button';
import { TextField } from '@/components';
import { SAVE_PASSWORD_AND_ACTIVATE_USER } from '@/apollo/mutations';
import { OverrideToggle } from '@/pages/Onboarding/components/OverrideToggle/OverrideToggle';
import { getProgram } from '@/utils/posthog';
import { useUserUid } from '@/hooks/useUserUid';
import { useUtmParams } from '@/hooks/useUtmParams';
import { isSpark as isSparkHelper } from '@/utils/productHelpers';
import { AssignmentTypeOfClient } from '@/apollo/__generated__/graphql';

const ContentContainer = styled(Box)({
  display: 'flex',
  flexDirection: 'column',
  flex: 1,
  marginTop: 8,
  '& > *': {
    marginTop: 24,
  },
});

const CriteriaWrapper = styled(Box)({
  display: 'flex',
  flexDirection: 'column',
  gap: 10,
  marginTop: 20,
});

const Label = styled(Typography)({
  display: 'inline',
});

const List = styled(Box)(({ theme }) => ({
  margin: 0,
  padding: 0,
  listStylePosition: 'inside',
  listStyle: 'disc',
  '& li::marker': {
    color: theme.palette.secondary.main,
  },
}));

type Props = {
  onNext: (authToken: string) => void;
  onError: () => void;
  isGeneric?: boolean;
};

const CreatePassword: React.FC<Props> = ({ onNext, onError, isGeneric = true }) => {
  const { t } = useTranslation();
  const { userUid } = useUserUid();
  const location = useLocation();
  const { utmParams } = useUtmParams();
  const { formState: { errors, isValid }, clearErrors, getValues } = useFormContext();
  const { careTeamUid, token, product } = useParams<SleepioWellnessOnboardingParams>();
  const [savePasswordAndActivateUser, { loading }] = useMutation(SAVE_PASSWORD_AND_ACTIVATE_USER);

  const [returnError, setReturnError] = useState(false);
  const [visibility, setVisibility] = useState({
    password: false,
    confirmPassword: false,
  });

  const posthogProgram = getProgram(product);
  const posthog = usePostHog();

  const toggleVisibility = (field: 'password' | 'confirmPassword') => {
    setVisibility((prev) => ({
      ...prev,
      [field]: !prev[field],
    }));
  };

  const getRedirectUri = () => {
    // Check if redirect_uri is present (indicates mobile app flow)
    if (location.search) {
      return new URLSearchParams(location.search)?.get('redirect_uri');
    }
    return null;
  };

  /**
   * Validates redirect URI for security.
   * CRITICAL: This function prevents open redirect attacks by blocking malicious URLs
   * like https://attacker.com that could steal auth tokens. Only deep link protocols
   * (sleepio:, spark:, etc.) are allowed for mobile app redirects.
   * CodeQL security analysis requires explicit validation of each URL component.
   */
  const isSafeRedirectUri = (uri: string): boolean => {
    try {
      const url = new URL(uri);

      // Reject URLs with username or password components
      if (url.username || url.password) {
        return false;
      }

      // Block dangerous web protocols
      const dangerousProtocols = ['http:', 'data:', 'file:', 'ftp:'];
      if (dangerousProtocols.includes(url.protocol) || url.protocol.startsWith('javascript')) {
        return false;
      }

      // Allow custom deep link protocols (sleepio:, spark:, etc.)
      return true;
    } catch {
      return false;
    }
  };

  /**
   * Redirects to mobile app with auth credentials.
   * Security: redirectUri is validated by isSafeRedirectUri() before calling this function.
   * Only safe deep link protocols (sleepio:, spark:) are allowed.
   * Note: URL reconstruction is required to satisfy CodeQL taint flow analysis.
   */
  const redirectToMobileApp = (
    redirectUri: string,
    authToken: string,
    refreshToken: string,
    userId: string,
    userEmail: string,
  ) => {
    const queryParams = new URLSearchParams({
      token: authToken,
      ...(refreshToken && { refresh_token: refreshToken }),
      ...(userId && { user_id: userId }),
      ...(userEmail && { user_email: userEmail }),
    });

    // Reconstruct URL from validated components to break taint flow for CodeQL
    const validatedUrl = new URL(redirectUri);
    const safeUrl = `${validatedUrl.protocol}//${validatedUrl.host}${validatedUrl.pathname}`;

    window.location.href = `${safeUrl}?${queryParams.toString()}`;
  };

  const onSubmit = async () => {
    posthog.capture('ActivationFlowCompleted', {
      program: posthogProgram,
      'wellness.cortex_id': userUid,
    });
    try {
      if (returnError) {
        throw new Error('Intentional error produced by the override toggle');
      }

      const passwordsData = getValues();

      const redirectUri = getRedirectUri();
      const isMobileFlow = !!redirectUri;

      const response = await savePasswordAndActivateUser({
        variables: {
          careTeamUid,
          password: passwordsData.password,
          token,
          clientType: isMobileFlow ? AssignmentTypeOfClient.Mobile : AssignmentTypeOfClient.WebPortal,
          ...utmParams,
        },
      });

      if (!response.data?.savePasswordAndActivateUser.success) {
        console.warn('Password wasn`t set', response.errors);
        onError();
        return;
      }
      const authData = response.data.savePasswordAndActivateUser.authToken;
      const authToken = authData?.token;
      const refreshToken = authData?.refreshToken;
      const userId = authData?.user?.uid;
      const userEmail = authData?.user?.email;

      if (!authToken) {
        console.warn('Auth token is missing in the response');
        onError();
        return;
      }

      if (redirectUri) {
        // Mobile app flow: validate and redirect to deep link
        if (!isSafeRedirectUri(redirectUri)) {
          console.error('Unsafe redirect_uri blocked:', redirectUri);
          onError();
          return;
        }

        redirectToMobileApp(redirectUri, authToken, refreshToken, userId, userEmail);
      } else {
        // Web portal flow: continue to next step
        onNext(authToken);
      }
    } catch (e) {
      console.warn('Error during mutation:', e);
      onError();
    }
  };

  const isSpark = isSparkHelper(product);
  const isMobileAppFlow = !!getRedirectUri();
  let flowType = 'default';
  if (isMobileAppFlow) { flowType = (isGeneric ? 'generic' : 'specific'); }
  const titleKey = isSpark
    ? 'coverage.spark.person_enrolling.spark_title'
    : `coverage.sleepio.patient.you_are_covered.${flowType}.title`;
  const subtitleKey = isSpark
    ? 'coverage.spark.person_enrolling.spark_subtitle'
    : `coverage.sleepio.patient.you_are_covered.${flowType}.subtitle`;
  return (
    <ContentContainer>
      <Box>
        <Typography variant="h2" style={{ marginBottom: 8 }}>
          {t(titleKey)}
        </Typography>
        <Typography>
          {t(subtitleKey)}
        </Typography>
      </Box>
      <Controller
        name="password"
        render={({ field }) => (
          <TextField
            {...field}
            data-testid="create-password-password"
            required
            label={t('coverage.sleepio.patient.password.label')}
            placeholder={t('coverage.sleepio.patient.password.placeholder')}
            helperText={errors?.password?.message}
            error={!!errors?.password}
            onFocus={() => clearErrors('password')}
            type={visibility.password ? 'text' : 'password'}
            InputLabelProps={{
              shrink: true,
            }}
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton
                    onClick={() => toggleVisibility('password')}
                    edge="end"
                  >
                    {visibility.password ? <Visibility /> : <VisibilityOff />}
                  </IconButton>
                </InputAdornment>
              ),
            }}
          />
        )}
      />
      <Controller
        name="confirmPassword"
        render={({ field }) => {
          const values = getValues();
          const validationState = getPasswordValidationState(values, errors, isValid, t);

          return (
            <TextField
              {...field}
              data-testid="create-password-confirm-password"
              required
              label={t('coverage.sleepio.patient.confirm_password.label')}
              placeholder={t('coverage.sleepio.patient.confirm_password.placeholder')}
              helperText={validationState.errorMessage}
              error={validationState.showError}
              onFocus={() => clearErrors('confirmPassword')}
              type={visibility.confirmPassword ? 'text' : 'password'}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      onClick={() => toggleVisibility('confirmPassword')}
                      edge="end"
                    >
                      {visibility.confirmPassword ? <Visibility /> : <VisibilityOff />}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />
          );
        }}
      />
      {!isSpark && (
        <CriteriaWrapper>
          <Label>
            {t('coverage.sleepio.patient.password_criteria.title')}
          </Label>
          <List component="ul">
            <li>
              <Label>{t('coverage.sleepio.patient.password_criteria.size')}</Label>
            </li>
            <li>
              <Label>{t('coverage.sleepio.patient.password_criteria.letter')}</Label>
            </li>
            <li>
              <Label>{t('coverage.sleepio.patient.password_criteria.number')}</Label>
            </li>
            <li>
              <Label>{t('coverage.sleepio.patient.password_criteria.special_char')}</Label>
            </li>
          </List>
        </CriteriaWrapper>
      )}
      <Button
        variant="contained"
        type="submit"
        fullWidth
        role="button"
        data-testid="submit-button"
        color="primary"
        loading={loading}
        disabled={!isValid || loading}
        rounded
        onClick={onSubmit}
        style={
          isSpark
            ? {
              backgroundColor: sparkTheme.palette.primary.main,
              color: sparkTheme.palette.getContrastText(sparkTheme.palette.primary.main),
              borderRadius: 100,
            }
            : undefined
        }
      >
        {t(`coverage.sleepio.patient.you_are_covered.${flowType}.button_text`)}
      </Button>
      {process.env.NODE_ENV !== 'production' && (
        <OverrideToggle
          userMessage="Simulate server error"
          override={setReturnError}
        />
      )}
    </ContentContainer>
  );
};

export default CreatePassword;
